import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

/* eslint-disable react/no-deprecated */
import React, { Component, useContext } from 'react';
import { scrollIntoView, safeInvoke } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import { SectionItem } from '../Section';
import Hint from '../Hint';
import Button from '../Button';
import Link from '../Link';
import Icon from '../Icon';
import Popover from '../Popover';
import Action from '../Action';
import Translate from '../Translate';
import Focuser from '../Overlay/Focuser';
import FormContext from '../Form/FormContext';
import VerticalContext from '../VerticalContext';
import ScrollableElementFormContext from '../Form/ScrollableElementFormContext';
import DisabledContext from '../Form/DisabledContext';
var defaultValue = undefined;
/**
 * Simple helper to handle situations when we expecting only array
 * @param value
 * @param nonEmpty
 * @returns {Array}
 */

var getArrayHelper = function getArrayHelper(value) {
  var nonEmpty = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  if (Array.isArray(value) && value.length > 0) {
    return value;
  }

  var result = [];

  if (nonEmpty) {
    result.push(undefined);
  }

  return result;
};

var isControlled = function isControlled(props) {
  return props.value !== undefined;
};
/**
 * @since 0.0.54
 */


var FormField =
/*#__PURE__*/
function (_Component) {
  _inherits(FormField, _Component);

  function FormField(_props) {
    var _this;

    _classCallCheck(this, FormField);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(FormField).call(this, _props));

    _defineProperty(_assertThisInitialized(_this), "getFieldApi", function (index) {
      if (!_this.props.multi) {
        return _this.fieldApi;
      }

      return {
        getId: function getId() {
          var id = _this.fieldApi.getId();

          return id ? "".concat(id, "-").concat(index) : id;
        },
        getName: function getName() {
          var name = _this.fieldApi.getName();

          return name ? "".concat(name, "[").concat(index, "]") : name;
        },
        getValue: function getValue(def) {
          var values = _this.fieldApi.getValue();

          if (values instanceof Array && values[index]) {
            return values[index];
          }

          return def;
        },
        setValue: function setValue(value) {
          var values = getArrayHelper(_this.fieldApi.getValue(), true);
          return _this.fieldApi.setValue(values.map(function (v, i) {
            return i === index ? value : v;
          }));
        },
        getErrors: function getErrors() {
          var errors = _this.fieldApi.getErrors();

          if (errors instanceof Array && errors[index]) {
            return errors[index];
          }

          return {};
        },
        getRequiredMark: function getRequiredMark() {
          if (index === 0) {
            return _this.fieldApi.getRequiredMark();
          }

          return null;
        },
        getSeparator: function getSeparator() {
          return React.createElement("span", {
            className: "".concat(_this.props.baseClassName, "__separator")
          });
        },
        isDisabled: function isDisabled() {
          return _this.props.disabled;
        },
        getFullDescription: function getFullDescription() {
          if (index === 0) {
            return _this.fieldApi.getFullDescription();
          }

          return null;
        }
      };
    });

    _defineProperty(_assertThisInitialized(_this), "focus", function (errors) {
      var index = 0;

      if (Array.isArray(errors)) {
        index = errors.findIndex(function (v) {
          return Boolean(v);
        });
      }

      var scrollableElement = _this.props.scrollableElement;
      var target = _this.targetRefs[index];

      if (target) {
        scrollIntoView(target, {
          boundary: scrollableElement
        });
        _this.focusTimer = setTimeout(function () {
          if (target) {
            new Focuser().focusFirstDescendant(target);
          }
        }, 500);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleAddMore", function () {
      var values = getArrayHelper(_this.fieldApi.getValue(), true);

      _this.fieldApi.setValue([].concat(_toConsumableArray(values), [defaultValue]));
    });

    _defineProperty(_assertThisInitialized(_this), "handleRemove", function (index) {
      var values = getArrayHelper(_this.fieldApi.getValue());

      _this.fieldApi.setValue(values.filter(function (value, valueIndex) {
        return valueIndex !== index;
      })); // Can be used for removing errors from form properties


      safeInvoke(_this.props.onRemoveRow, index); // Remove errors from component state

      _this.setState(function (prevState) {
        if (prevState.errors && prevState.errors.filter) {
          return {
            errors: prevState.errors.filter(function (error, errorIndex) {
              return errorIndex !== index;
            })
          };
        }

        return {};
      });
    });

    _defineProperty(_assertThisInitialized(_this), "setTargetRef", function (index) {
      return function (node) {
        _this.targetRefs[index] = node;
      };
    });

    _defineProperty(_assertThisInitialized(_this), "renderRemoveButton", function (index) {
      var _this$props = _this.props,
          multi = _this$props.multi,
          locale = _this$props.locale,
          disabled = _this$props.disabled;
      var value = getArrayHelper(_this.fieldApi.getValue());

      if (!multi || value.length <= 1) {
        return null;
      }

      return React.createElement(Button, {
        className: "".concat(_this.props.baseClassName, "__remove"),
        icon: "cross-mark",
        tooltip: React.createElement(Translate, {
          namespace: "FormField",
          content: "remove",
          fallback: "Remove",
          translators: locale
        }),
        onClick: function onClick() {
          return _this.handleRemove(index);
        },
        disabled: disabled,
        ghost: true
      });
    });

    _defineProperty(_assertThisInitialized(_this), "renderDescription", function (index) {
      var _this$props2 = _this.props,
          baseClassName = _this$props2.baseClassName,
          description = _this$props2.description,
          multi = _this$props2.multi;

      if (!description || multi && _this.state.value.length - 1 > index) {
        return null;
      }

      return React.createElement(Hint, {
        className: "".concat(baseClassName, "__description")
      }, description);
    });

    _defineProperty(_assertThisInitialized(_this), "renderField", function () {
      var _classNames;

      var index = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var renderAddMore = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var _this$props3 = _this.props,
          baseClassName = _this$props3.baseClassName,
          className = _this$props3.className,
          id = _this$props3.id,
          name = _this$props3.name,
          value = _this$props3.value,
          onChange = _this$props3.onChange,
          formFieldLabel = _this$props3.label,
          description = _this$props3.description,
          errors = _this$props3.errors,
          required = _this$props3.required,
          children = _this$props3.children,
          multi = _this$props3.multi,
          disabled = _this$props3.disabled,
          addMoreValidator = _this$props3.addMoreValidator,
          onRemoveRow = _this$props3.onRemoveRow,
          locale = _this$props3.locale,
          form = _this$props3.form,
          vertical = _this$props3.vertical,
          scrollableElement = _this$props3.scrollableElement,
          fullDescription = _this$props3.fullDescription,
          props = _objectWithoutProperties(_this$props3, ["baseClassName", "className", "id", "name", "value", "onChange", "label", "description", "errors", "required", "children", "multi", "disabled", "addMoreValidator", "onRemoveRow", "locale", "form", "vertical", "scrollableElement", "fullDescription"]);

      var fieldApi = _this.getFieldApi(index);

      var fieldErrors = fieldApi.getErrors();
      var hasErrors = fieldErrors && Object.keys(fieldErrors).length > 0;
      var label = formFieldLabel;

      if (label && index) {
        label = '';
      }

      var innerField = typeof children === 'function' ? children(fieldApi) : children;

      if (multi) {
        innerField = React.createElement("div", {
          className: "".concat(baseClassName, "__multi")
        }, innerField, " ", _this.renderRemoveButton(index));
      }

      return React.createElement(SectionItem, _extends({
        className: classNames(baseClassName, (_classNames = {}, _defineProperty(_classNames, "".concat(baseClassName, "--error"), hasErrors), _defineProperty(_classNames, "".concat(baseClassName, "--vertical-multi"), multi && vertical), _classNames), className),
        title: label === null ? null : React.createElement("div", {
          className: "".concat(baseClassName, "__label")
        }, React.createElement("label", {
          htmlFor: fieldApi.getId()
        }, label), fieldApi.getFullDescription(), required ? fieldApi.getRequiredMark() : null),
        key: index,
        vertical: vertical
      }, props), React.createElement("div", {
        ref: _this.setTargetRef(index)
      }, innerField), hasErrors && Object.keys(fieldErrors).map(function (key) {
        return React.createElement("span", {
          key: key,
          className: "".concat(baseClassName, "__error")
        }, React.createElement(Icon, {
          className: "".concat(baseClassName, "__error-icon"),
          name: "exclamation-mark-circle-filled",
          size: "12"
        }), fieldErrors[key]);
      }), _this.renderDescription(index), renderAddMore && React.createElement(Link, {
        onClick: _this.handleAddMore,
        className: "".concat(baseClassName, "__add-more"),
        disabled: disabled
      }, React.createElement(Translate, {
        namespace: "FormField",
        content: "addMore",
        fallback: "Add one more",
        translators: locale
      })));
    });

    var _this$props4 = _this.props,
        _value = _this$props4.value,
        _multi = _this$props4.multi;
    var valueInState = _value;

    if (_multi && !valueInState) {
      valueInState = [defaultValue];
    } // eslint-disable-next-line react/state-in-constructor


    _this.state = {
      value: valueInState,
      errors: _this.props.errors
    };
    _this.fieldApi = {
      getId: function getId() {
        if (_this.props.id) {
          return _this.props.id;
        }

        if (_this.props.name) {
          return _this.props.name.replace(/[[\]]+/g, '-').replace(/-+$/, '');
        }

        return null;
      },
      getName: function getName() {
        return _this.props.name;
      },
      getValue: function getValue(def) {
        if (_this.props.form && _this.props.name) {
          return _this.props.form.getValue(_this.props.name, def);
        }

        return _this.state.value || def;
      },
      setValue: function setValue(value) {
        _this.setState({
          value: value
        });

        if (_this.props.form && _this.props.name) {
          _this.props.form.setValue(_this.props.name, value);
        }

        safeInvoke(_this.props.onChange, value);
      },
      getErrors: function getErrors() {
        if (_this.props.form && _this.props.name) {
          return _this.props.form.getErrors(_this.props.name);
        }

        return _this.state.errors;
      },
      getRequiredMark: function getRequiredMark() {
        if (_this.props.form) {
          return _this.props.form.getRequiredMark();
        }

        return null;
      },
      isDisabled: function isDisabled() {
        return Boolean(_this.props.disabled);
      },
      getFullDescription: function getFullDescription() {
        if (!_this.props.fullDescription) {
          return null;
        }

        return React.createElement(Popover, {
          target: React.createElement(Action, {
            outerProps: {
              className: "".concat(_this.props.baseClassName, "__full-description")
            },
            icon: {
              name: 'info-circle',
              size: '12',
              className: "".concat(_this.props.baseClassName, "__full-description-icon")
            }
          }),
          placement: "right",
          intent: "info"
        }, _this.props.fullDescription);
      }
    };
    _this.targetRefs = [];
    return _this;
  }

  _createClass(FormField, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (this.props.form) {
        this.props.form.setRequiredField(this.props.name, this.props.required);
        this.props.form.registerField(this.props.name, {
          focus: this.focus
        });
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (this.props.form) {
        this.props.form.setRequiredField(this.props.name, false);
        this.props.form.unregisterField(this.props.name);
      }

      clearTimeout(this.focusTimer);
    }
    /**
     * Field API provided to field
     * @param index
     * @returns {Object}
     */

  }, {
    key: "render",
    value: function render() {
      if (!this.props.multi) {
        return this.renderField();
      }

      var index = 0;
      var count = (this.fieldApi.getValue() || []).length || 1;
      var lines = [];

      do {
        lines.push(this.renderField(index++, count === index));
      } while (index < count);

      return lines;
    }
  }], [{
    key: "getDerivedStateFromProps",
    value: function getDerivedStateFromProps(props, state) {
      var nextState = {};
      var errors = props.errors,
          form = props.form,
          required = props.required,
          name = props.name;
      var prevErrors = state.prevErrors,
          prevRequired = state.prevRequired;

      if (isControlled(props)) {
        nextState.value = props.value;
      }

      if (errors !== prevErrors) {
        nextState.errors = errors;
      }

      if (form && required !== prevRequired) {
        form.setRequiredField(name, required);
      }

      nextState.prevErrors = errors;
      nextState.prevRequired = required;
      return nextState;
    }
  }]);

  return FormField;
}(Component);

var propTypes = {
  /**
   * Field ID.
   * @since 0.0.59
   */
  id: PropTypes.string,

  /**
   * Field name.
   * @since 0.0.54
   */
  name: PropTypes.string,

  /**
   * Field value.
   * @deprecated Use [Form](#!/Form) values.
   * @since 0.0.57
   */
  value: PropTypes.any,

  /**
   * A callback function, can be executed when the value is changing.
   * @since 0.4.1
   */
  onChange: PropTypes.func,

  /**
   * Field label.
   * @since 0.0.54
   */
  label: PropTypes.any,

  /**
   * Field description.
   * @since 0.0.57
   */
  description: PropTypes.any,

  /**
   * Show long descriptions in popover after clicking on "( i )" icon
   * @since 3.14.0
   */
  fullDescription: PropTypes.node,

  /**
   * Field errors.
   *  @deprecated Use [Form](#!/Form) errors.
   * @since 0.0.57
   */
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

  /**
   * Indicates whether field is required.
   * @since 0.0.54
   */
  required: PropTypes.bool,

  /**
   * Content of the `FormField`.
   * @since 0.0.54
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * If 'true', a field will be multiple
   * @since 1.12.0
   */
  multi: PropTypes.bool,

  /**
   * @ignore Disabled state of multiple field.
   */
  disabled: PropTypes.bool,

  /**
   * Localization messages
   * @since 1.12.0
   */
  locale: PropTypes.shape({
    addMore: PropTypes.node,
    remove: PropTypes.node
  }),

  /**
   * Callback which called for all values. If it returns 'true' a pseudo-link 'Add more' will be disabled
   * @ignore
   * @deprecated
   */
  addMoreValidator: PropTypes.func,

  /**
   * A callback function, can be executed when the row is removed.
   * @since 1.12.0
   */
  onRemoveRow: PropTypes.func,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string,

  /**
   * @ignore
   */
  form: PropTypes.object,

  /**
   * Vertical or horizontal field view.
   * If it is not defined explicitly, the vertical mode will use from Form or Section components.
   * @since 3.13.0
   */
  vertical: PropTypes.bool,

  /**
   * @ignore
   */
  scrollableElement: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.instanceOf(Window)])
};
var defaultProps = {
  id: undefined,
  name: undefined,
  value: defaultValue,
  onChange: null,
  label: undefined,
  description: undefined,
  errors: {},
  required: false,
  children: undefined,
  multi: undefined,
  disabled: undefined,
  locale: undefined,
  addMoreValidator: undefined,
  onRemoveRow: null,
  fullDescription: undefined,
  className: undefined,
  baseClassName: "".concat(CLS_PREFIX, "form-field"),
  form: undefined,
  vertical: undefined,
  scrollableElement: undefined
};
FormField.propTypes = propTypes;
FormField.defaultProps = defaultProps;
/**
 * `FormField` component is used for showing of parameter values in Form or
 * for entering of user data with any form elements.
 *
 * For the most cases the following form elements can be used:
 * * [FormFieldText](#!/FormFieldText) for entering text,
 * * [FormFieldSelect](#!/FormFieldSelect), [FormFieldCheckbox](#!/FormFieldCheckbox),
 * [FormFieldRadioButtons](#!/FormFieldRadioButtons) for selecting options.
 */

var FormFieldWrapper = function FormFieldWrapper(props) {
  var form = useContext(FormContext);
  var verticalContext = useContext(VerticalContext);
  var vertical = props.vertical === undefined ? verticalContext : props.vertical;
  var scrollableElement = useContext(ScrollableElementFormContext);
  var disabled = useContext(DisabledContext);
  return React.createElement(FormField, _extends({}, props, {
    form: form,
    vertical: vertical,
    scrollableElement: scrollableElement,
    disabled: props.disabled === undefined ? disabled : props.disabled
  }));
};

FormFieldWrapper.displayName = 'FormField';
FormFieldWrapper.propTypes = propTypes;
FormFieldWrapper.defaultProps = defaultProps;
export default FormFieldWrapper;